import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/ClientShared.css";

const initialFormData = {
  businessName: "",
  workflowGoal: "",
  currentProcess: "",
  processUsers: "",
  currentTools: "",
  customerSteps: "",
  adminSteps: "",
  dataCollected: "",
  businessRules: "",
  notifications: "",
  payments: "",
  userRoles: "",
  savedData: "",
  customerView: "",
  adminView: "",
  integrations: "",
  failureRisks: "",
  launchMustHaves: "",
  futureIdeas: "",
};

const sections = [
  {
    title: "Workflow Goal",
    description: "Define what this custom build is supposed to improve or replace.",
    fields: [
      { name: "businessName", label: "Business name", type: "input", required: true },
      { name: "workflowGoal", label: "What workflow are we replacing or improving?", type: "textarea", required: true },
      { name: "currentProcess", label: "Walk through the current process step by step.", type: "textarea", required: true },
      { name: "processUsers", label: "Who uses this workflow? Customers, admins, staff, contractors, etc.", type: "textarea" },
      { name: "currentTools", label: "What tools are involved today?", type: "textarea", placeholder: "Calendly, Stripe, Google Forms, spreadsheets, email, CRM, etc." },
    ],
  },
  {
    title: "Customer & Admin Flow",
    description: "Separate what the customer sees from what the business needs behind the scenes.",
    fields: [
      { name: "customerSteps", label: "What should the customer do from start to finish?", type: "textarea", required: true },
      { name: "adminSteps", label: "What should you or your team do after the customer submits/acts?", type: "textarea" },
      { name: "customerView", label: "What does the customer need to see?", type: "textarea" },
      { name: "adminView", label: "What does the admin/business owner need to see?", type: "textarea" },
      { name: "userRoles", label: "Who needs access, and what should each role be allowed to do?", type: "textarea" },
    ],
  },
  {
    title: "Logic, Data & Integrations",
    description: "These answers help lock scope before development starts.",
    fields: [
      { name: "dataCollected", label: "What information needs to be collected?", type: "textarea", required: true },
      { name: "businessRules", label: "What rules or conditions matter?", type: "textarea", placeholder: "Example: if service area is outside Philly, show different message." },
      { name: "notifications", label: "What emails, confirmations, or notifications should be sent?", type: "textarea" },
      { name: "payments", label: "Are payments, deposits, invoices, or checkout needed?", type: "textarea" },
      { name: "savedData", label: "What data needs to be saved for later?", type: "textarea" },
      { name: "integrations", label: "Any third-party tools that need to connect?", type: "textarea" },
    ],
  },
  {
    title: "Scope & Risk",
    description: "This protects the project from surprises and keeps the build realistic.",
    fields: [
      { name: "failureRisks", label: "What would break the business process if this workflow is wrong?", type: "textarea", required: true },
      { name: "launchMustHaves", label: "What absolutely must be working at launch?", type: "textarea", required: true },
      { name: "futureIdeas", label: "What ideas should be saved for later, but not required for launch?", type: "textarea" },
    ],
  },
];

const AuthorityProcessAudit = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        setProjectId(userData.projectId);
        const projectSnap = await getDoc(doc(db, "projects", userData.projectId));
        if (projectSnap.exists()) {
          setFormData({ ...initialFormData, ...(projectSnap.data().authorityProcessAudit || {}) });
        }
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load process audit.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      "businessName",
      "workflowGoal",
      "currentProcess",
      "customerSteps",
      "dataCollected",
      "failureRisks",
      "launchMustHaves",
    ];

    const missing = requiredFields.some((field) => !formData[field]);
    if (missing) {
      setError("Please complete the required process-audit fields before saving.");
      toast.warn("Please complete the required fields.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (!projectId) throw new Error("Project ID not set.");
      await updateDoc(doc(db, "projects", projectId), {
        authorityProcessAudit: formData,
      });
      toast.success("Process audit saved.");
    } catch (err) {
      setError("Failed to save process audit: " + err.message);
      toast.error("Failed to save process audit.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading process audit...</p></div>;
  }

  return (
    <div className="cp-page">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Authority Process Audit</h1>
        <p className="cp-subtitle">
          Authority projects need more than design direction. This locks down the workflow, data, logic, and risks before custom development starts.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {sections.map((section) => (
          <div key={section.title} className="cp-section" style={{ marginBottom: "20px" }}>
            <div className="cp-section-header">
              <div>
                <h2 className="cp-section-title">{section.title}</h2>
                <p className="cp-empty-text" style={{ marginTop: "4px" }}>{section.description}</p>
              </div>
            </div>
            <div className="cp-section-body">
              {section.fields.map((field) => (
                <div key={field.name} className="cp-form-group">
                  <label>
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>
                  {field.type === "input" ? (
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  ) : (
                    <textarea
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="cp-message--error">{error}</p>}

        <div className="cp-btn-row">
          <button type="submit" className="cp-btn-primary" disabled={saving || !projectId}>
            {saving ? "Saving..." : "Save Process Audit"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AuthorityProcessAudit;
